import { getId, replaceProperties } from '@shared/helpers/object.helpers';
import { Shape, Storey } from '@shared/models/design/storey.model';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { ArraySplice } from 'type-fest';
import { mutateShape } from './shape.mutator';
import { replaceIdItem, shallowEqualMap } from '@shared/helpers/array.helpers';
import { createShape } from '../factories/shape.factory';
import { Point2D } from '@shared/models/geometry.model';
import { getShapeByIdFromStorey } from '../storey-shape.helpers';

const updateStorey = (storey: Storey, updates: Partial<Storey>): Storey => {
  return replaceProperties(storey, updates);
};

/**
 * Remove a shape from a storey
 * @param storey - The storey to remove the shape from
 * @param shapeOrIds - The shape or id of the shape to remove
 * @returns The updated storey (if any changes were made)
 */
const removeShapes = (
  storey: Storey,
  ...shapeOrIds: ItemOrItemId<Shape>[]
): Storey => {
  const toRemove = shapeOrIds.map(getId);
  const updatedShapes = storey.shapes.filter((s) => !toRemove.includes(s.id));
  if (updatedShapes.length === storey.shapes.length) return storey;
  return {
    ...storey,
    shapes: updatedShapes,
  };
};

/**
 * Add a shape to a storey in a design
 * @param design - The design to update
 * @param storeyOrId - The storey or id of the storey to add the shape to
 * @param shape - The shape to add
 * @returns The updated design (if any changes were made)
 */
const addShapes = (
  storey: Storey,
  ...shapes: Parameters<typeof createShape>[0][]
): Storey => {
  return updateStorey(storey, {
    ...storey,
    shapes: [...storey.shapes, ...shapes.map((s) => createShape(s))],
  });
};

/**
 * Replace a shape in a storey
 * @param storey - The storey to replace the shape in
 * @param update - The update to apply to the shape
 * @returns
 */
const replaceShape = (storey: Storey, update: Shape): Storey => {
  return updateStorey(storey, {
    shapes: replaceIdItem(storey.shapes, update),
  });
};

/**
 * Wrap a shape mutator function to mutate a storey
 * @param mutatorFn - The mutator function to use
 * @returns A function that mutates a shape in a storey
 */
const shapeMutatorFnFactory = <
  T extends (shape: Shape, ...args: any[]) => Shape,
>(
  mutatorFn: T,
): ((
  storey: Storey,
  shapeOrId: ItemOrItemId<Shape>,
  ...args: ArraySplice<Parameters<T>, 0, 1>
) => Storey) => {
  return (storey, shapeOrId, ...args) => {
    const shape = getShapeByIdFromStorey(storey, shapeOrId);
    if (!shape) return storey;
    return replaceShape(storey, mutatorFn(shape, ...args));
  };
};

/**
 * Update one or many shapes in a storey
 * @param storey - The storey to update the shapes in
 * @param updates - The updates to apply to the shapes
 * @returns The updated storey (if any changes were made)
 */
const updateShapes = (
  storey: Storey,
  ...updates: SemiPartial<Shape, 'id'>[]
): Storey => {
  const updatedShapes = shallowEqualMap(storey.shapes, (shape) => {
    for (const update of updates.filter((u) => u.id === shape.id)) {
      shape = mutateShape.update(shape, update);
    }
    return shape;
  });
  return updateStorey(storey, {
    shapes: updatedShapes,
  });
};

const translateAllPoints = (storey: Storey, delta: Point2D): Storey => {
  return updateStorey(storey, {
    shapes: storey.shapes.map((shape) =>
      mutateShape.point.translateMany(shape, delta),
    ),
  });
};

/**
 * Remove all points at a position in all shapes in a storey
 * @param storey - The storey to remove the points from
 * @param position - The position to remove the points at
 * @returns The updated storey (if any changes were made)
 */
const removeAllPointsAt = (storey: Storey, position: Point2D): Storey => {
  const updatedShapes = shallowEqualMap(storey.shapes, (shape) => {
    return mutateShape.point.remove(shape, position);
  });
  return updateStorey(storey, {
    shapes: updatedShapes,
  });
};

/**
 * Utils to mutate a storey
 */
export const mutateStorey = {
  update: updateStorey,
  shape: {
    add: addShapes,
    remove: removeShapes,
    update: updateShapes,
    point: {
      move: shapeMutatorFnFactory(mutateShape.point.move),
      remove: shapeMutatorFnFactory(mutateShape.point.remove),
      insertAt: shapeMutatorFnFactory(mutateShape.point.insertAt),
      set: shapeMutatorFnFactory(mutateShape.point.set),
      add: shapeMutatorFnFactory(mutateShape.point.add),
      translateMany: shapeMutatorFnFactory(mutateShape.point.translateMany),
    },
  },
  all: {
    points: {
      translate: translateAllPoints,
    },
  },
  many: {
    points: {
      removeAt: removeAllPointsAt,
    },
  },
} as const;
